import Api from '@/utils/api'
import axios from 'axios'

// 萤石云开放平台 API 基础地址
const YS7_BASE_URL = 'https://open.ys7.com/api/'

// 使用 api.js 导出的 axios 实例
// api.js 已经配置了萤石云 API 的自动识别和 token 处理
// 无需创建独立实例和自定义拦截器

// 统一处理萤石云 API 请求，自动添加 axiosType: 'ys7'
const ys7Request = {
  get: (url, config = {}) => {
    // GET 请求：将 axiosType 添加到 params 中
    const params = { ...(config.params || {}), axiosType: 'ys7' }
    return Api.get(url, { ...config, params })
  },
  post: (url, body, config = {}) => {
    // POST 请求：将 axiosType 添加到 body 中
    return Api.post(url, { ...body, axiosType: 'ys7' }, config)
  },
  put: (url, body, config = {}) => {
    // PUT 请求：将 axiosType 添加到 body 中
    return Api.put(url, { ...body, axiosType: 'ys7' }, config)
  }
}

// 获取 accessToken
// 注意：此接口不需要 accessToken，所以直接传递 body 对象，api.js 不会自动添加 accessToken
export function getAccessToken(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/token/get`, body)
}

/**
 * 分页查询当前 accessToken 对应萤石账号下「已绑定设备」列表（含 IPC、NVR 等）。
 * 官方 lapp/device/list：绑定设备列表，不包含「托管给开发者应用」的那类托管关系；托管设备需走 lapp/trust/*。
 */
export function getDeviceList(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/list`, body)
}

/**
 * 获取托管权限 token（多 C 端用户托管至开发者应用）。
 * 仅需 appKey、appSecret；勿自动注入账号 accessToken。
 * 文档：https://open.ys7.com/help/822
 */
export function getTrustDeviceV2Token(body) {
  return ys7Request.post(
    `${YS7_BASE_URL}lapp/trust/device/v2/token/get`,
    body,
    { skipYs7AccessTokenInject: true }
  )
}

// 获取设备信息
export function getDeviceInfo(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/info`, body)
}

// 获取视频流地址
export function getLiveAddress(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/v2/live/address/get`, body)
}

// 获取摄像头列表
export function getCameraList(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/camera/list`, body)
}

// 关闭设备视频加密
export function closeDeviceEncrypt(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/encrypt/off`, body)
}

// 设置设备撤/布防
export function setDeviceDefence(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/defence/set`, body)
}

// 开始云台控制
// 文档：https://open.ys7.com/help/679
// 接口路径：lapp/device/ptz/start
// 说明：对设备进行开始云台控制，开始云台控制之后必须先调用停止云台控制接口才能进行其他操作，包括其他方向的云台转动
// 参数：
//   - deviceSerial: String (是) - 设备序列号，存在英文字母的设备序列号时，字母需为大写
//   - channelNo: int (是) - 通道号
//   - direction: int (是) - 操作命令：0-上，1-下，2-左，3-右，4-左上，5-左下，6-右上，7-右下，8-物理放大，9-物理缩小，10-调整近焦距，11-调整远焦距，16-自动控制
//   - speed: int (是) - 云台速度：0-慢，1-适中，2-快，海康设备参数不可为 0
// 子账户 token 请求所需最小权限：`"Permission":"Ptz" "Resource":"Cam:序列号:通道号"`
// api.js 会自动从 Redux 获取 accessToken 并添加到 body 中
export function startDevicePtz(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/ptz/start`, body)
}

// 停止云台控制
// 文档：https://open.ys7.com/help/680
// 接口路径：lapp/device/ptz/stop
// 说明：设备停止云台控制
// 参数：
//   - deviceSerial: String (是) - 设备序列号，存在英文字母的设备序列号时，字母需为大写
//   - channelNo: int (是) - 通道号
//   - direction: int (否) - 操作命令：0-上，1-下，2-左，3-右，4-左上，5-左下，6-右上，7-右下，8-放大，9-缩小，10-近焦距，11-远焦距，16-自动控制
// 子账户 token 请求所需最小权限：`"Permission":"Ptz" "Resource":"Cam:序列号:通道号"`
// api.js 会自动从 Redux 获取 accessToken 并添加到 body 中
export function stopDevicePtz(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/ptz/stop`, body)
}

// 镜像翻转
// 文档：https://open.ys7.com/help/681
// 接口路径：lapp/device/ptz/mirror
// 说明：对设备进行镜像翻转操作（需要设备支持）
// 参数：
//   - deviceSerial: String (是) - 设备序列号，存在英文字母的设备序列号时，字母需为大写
//   - channelNo: int (是) - 通道号
//   - command: int (是) - 镜像方向：0-上下，1-左右，2-中心
// 子账户 token 请求所需最小权限：`"Permission":"Ptz" "Resource":"Cam:序列号:通道号"`
// api.js 会自动从 Redux 获取 accessToken 并添加到 body 中
export function setDeviceMirrorFlip(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/ptz/mirror`, body)
}

// 按时间获取录像列表
export function getVideoListByTime(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/video/by/time`, body)
}

// 获取回放视频流地址
export function getPlaybackAddress(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/v2/live/address/get`, body)
}

// 获取人员计算算法统计信息

// 注意：此接口路径为 service/intelligence/... 而不是 lapp/service/intelligence/...
export function getPersonCount(body) {
  return ys7Request.post(`${YS7_BASE_URL}service/intelligence/human/counting/detect`, body)
}

// 通用智能检测算法接口
export function getIntelligenceDetect(body) {
  return ys7Request.post(
    `${YS7_BASE_URL}service/intelligence/${body.algorithmPath}`, 
    body
  )
}

// 设置设备智能检测开关状态
export function setIntelligenceConfig(body) {
  return ys7Request.post(
    `${YS7_BASE_URL}lapp/device/intelligence/detection/switch/set`, 
    body
  )
}

// 获取设备智能检测开关状态
export function getIntelligenceConfig(body) {
  return ys7Request.post(
    `${YS7_BASE_URL}lapp/device/intelligence/detection/switch/status`, 
    body
  )
}

// 获取人形追踪开关状态
// api.js 会自动从 Redux 获取 accessToken 并添加到 Header 中
// GET 请求：通过 extractToHeaders 配置，将 deviceSerial 和 channelNo 添加到 Header
export function getHumanTrackSwitch(body) {
  return ys7Request.get(`${YS7_BASE_URL}v3/device/switch/human/track`, {
    params: body,
    extractToHeaders: {
      deviceSerial: body.deviceSerial,
      channelNo: body.channelNo,
      'Content-Type': 'application/x-www-form-urlencoded'
    } // 配置：将 deviceSerial 和 channelNo 添加到 Header
  })
}

// 配置人形追踪开关状态

// 通过 extractToHeaders 配置，将 deviceSerial 添加到 Header
export function setHumanTrackSwitch(body) {
  return ys7Request.put(`${YS7_BASE_URL}v3/device/switch/human/track`, 
    body,
    {
      extractToHeaders: {
        deviceSerial: body.deviceSerial,
        channelNo: body.channelNo
      } // 配置：将 deviceSerial 添加到 Header
    }
  )
}

// 查询设备支持算法列表
// api.js 会自动从 Redux 获取 accessToken 并添加到 Header 中
// GET 请求：通过 extractToHeaders 配置，将 deviceSerial 添加到 Header
export function getDeviceSupportAlgorithms(body) {
  return ys7Request.get(`${YS7_BASE_URL}v3/intelligent/model/device/support`, {
    params: body,
    extractToHeaders: {
      'Content-Type': 'application/x-www-form-urlencoded'
    //   deviceSerial: body.deviceSerial
    } // 配置：将 deviceSerial 添加到 Header
  })
}

// 设备抓拍图片

// 注意：该接口仅适用于 IPC 或者关联 IPC 的 DVR 设备
// 需要设备支持能力集：support_capture=1
// 建议每个摄像头调用的间隔 4s 以上
// quality 参数可选：0-流畅，1-高清(720P)，2-4CIF，3-1080P，4-400w（注：此参数不生效）
export function captureDevicePicture(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/device/capture`, body)
}

// 获取所有设备告警信息列表
// 文档：https://open.ys7.com/help/986
// 接口路径：lapp/alarm/list

export function getAlarmList(body) {
  return ys7Request.post(`${YS7_BASE_URL}lapp/alarm/list`, body)
}

// 设置设备视频类开关状态（包括视频水印开关）
// 文档：https://open.ys7.com/help/4376
// 接口路径：v3/device/video/switch/status
// type: 702-视频水印是否展示 LOGO
// api.js 会自动从 Redux 获取 accessToken 并添加到 Header 中
export function setDeviceVideoSwitch(body) {
  const { deviceSerial, ...rest } = body
  return ys7Request.put(
    `${YS7_BASE_URL}v3/device/video/switch/status`,
    rest,
    {
      extractToHeaders: {
        deviceSerial: deviceSerial
      }
    }
  )
}

// 查询设备视频类开关状态（包括视频水印开关）
// 文档：https://open.ys7.com/help/4377
// 接口路径：v3/device/video/switch/status
// type: 702-视频水印是否展示 LOGO
// api.js 会自动从 Redux 获取 accessToken 并添加到 Header 中
export function getDeviceVideoSwitch(body) {
  const { deviceSerial, ...rest } = body
  return ys7Request.get(`${YS7_BASE_URL}v3/device/video/switch/status`, {
    params: rest,
    extractToHeaders: {
      deviceSerial: deviceSerial
    }
  })
}
